import { gql } from "@apollo/client";
import { getFields, getOrderByWithSecondOrder } from "../../util/graphqlUtil";
import { QUERY_PAGE_SIZE } from "../util";
import { IPagedKursusQuery } from "../../interfaces/kursus";
import {
  kursusholdFields,
  KURSUS_FIELDS,
  fmuProeveDeltagerInfoFields,
} from "./fields";
import { KursusholdDtoFilterInput } from "@/interfaces/schema";
import { f } from "@/util/f";

export const GET_KURSUS_AUTOCOMPLETE_QUERY = ({ q: input }: { q: string }) => {
  const q = input.replaceAll('\\"', "").replaceAll('"', "");
  const where = f<KursusholdDtoFilterInput>()
    .addFilter("or", [
      {
        navn: {
          contains: q,
        },
      },
    ])
    .build();
  return gql`
    query pagedKursus {
      kursusholds(
        first: 5, 
        where: ${where}
        ) {
        edges {
          node {
            id
            navn
            holdNr
            kursustype
            fmukursusKategoriNavigation {
              navn
            }
          }
        }
      }
    }
  `;
};

export const KURSUS_BY_ID_TICKETDATA = gql`
  query kursusById_ticketdata($id: UUID!) {
    kursushold(id: $id) {
      id
      navn
      holdNr
      kursustype
      fmukursusKategoriNavigation {
        navn
      }
    }
  }
`;

export const GET_KURSUS_LINJE_AUTOCOMPLETE_QUERY = ({
  q: input,
}: {
  q: string;
}) => {
  const q = input.replaceAll('\\"', "").replaceAll('"', "");
  const parts = q.split("-");
  const queryIsCompleteLinjeNavn =
    parts.length === 2 &&
    !isNaN(parts[0] as any) &&
    parts[0].length === 4 &&
    parts[1].length > 0;

  return gql`
    query kursus_autocomplete {
      fmulinjerByNr: fmulinjer(
        first: 1
        where: { nr: { eq: ${isNaN(parseInt(q)) ? -1 : parseInt(q)} } }
      ) {
        edges {
          node {
            id
            navn
            nr
          }
        }
      }
      kursusholdsByNr: kursusholds(
        first: 1
        where: { holdNr: { eq: ${isNaN(parseInt(q)) ? -1 : parseInt(q)} } }
      ) {
        edges {
          node {
            id
            navn
            holdNr
            fmukursusKategoriNavigation {
              navn
            }
          }
        }
      }
      fmulinjer(first: ${
        queryIsCompleteLinjeNavn ? 1 : 2
      }, where: { navn: { contains: "${q}" } }) {
        edges {
          node {
            id
            navn
            nr
          }
        }
      }
      kursusholds(first: ${
        queryIsCompleteLinjeNavn ? 8 : 3
      }, where: { navn: { contains: "${q}" } }) {
        edges {
          node {
            id
            navn
            holdNr
            fmukursusKategoriNavigation {
              navn
            }
          }
        }
      }
    }
  `;
};

export const GET_PAGED_KURSUS_QUERY = (query: IPagedKursusQuery) => {
  const { after, before, fields, filters } = query;
  const nodeFields = getFields(fields);
  const order = getOrderByWithSecondOrder(query, "holdNr", "ASC");

  return gql`
      query pagedKursus {
      kursusholds(
          ${before ? "last: " + QUERY_PAGE_SIZE : "first: " + QUERY_PAGE_SIZE}
          ${after ? `after: "${after}"` : before ? `before: "${before}"` : ""}
          ${filters}
          ${order}
          ) {
          totalCount
          pageInfo {
            startCursor
            endCursor
            hasNextPage
            hasPreviousPage
          }
          edges {
            node {
              ${nodeFields}
              }
          }
        }
      }
    `;
};

export const GET_DELTAGER_BY_KURSUSID_QUERY = () => gql`
    query kursusholdById($id: UUID!, $dato: Date!) {
      kursushold(id: $id) {
        kursusPerson {
          ${KURSUS_FIELDS.kursusPerson}
          person{
            prioriteretEmail
            primaerPersonFirmaRelationPaa(dato: $dato) {
              periode {
                fraDato
                tilDatoInklusiv
              }
              id
              emailArbejde
              firma {
                id
                navn1
              }
            }
          }
        }
      }
    }
  `;

export const GET_KURSUSHOLD_BY_ID_QUERY = () => {
  return gql`
    query kursusholdById($id: UUID!, $date: Date!) {
      kursushold(id: $id) {
        ${kursusholdFields}
        kursusPerson {
        ${KURSUS_FIELDS.kursusPerson}
          person {
            primaerPersonFirmaRelationPaa(dato: $date) {
              periode {
                fraDato
                tilDatoInklusiv
              }
              emailArbejde
              firma {
                id
                navn1
                tlf
                email
              }
            }
          }
        }
        kursusholdUnderviser {
          ${KURSUS_FIELDS.kursusholdUnderviser}
          person {
            primaerPersonFirmaRelationPaa(dato: $date) {
              periode {
                fraDato
                tilDatoInklusiv
              }
              emailArbejde
              firma {
                id
                navn1
                email
                tlf
              }
            }
          }
        }
      }
    }
  `;
};

export const GET_KURSUSHOLD_KORRESPONDANCE_BY_ID_QUERY = () => gql`
  query kursusholdById($id: UUID!) {
    kursushold(id: $id) {
      kursusKorrespondance {
        id
        sendt
        emne
        antalModtagere
        opretDato
        opretInit
        retteDato
        retteInit
        dokument
      }
    }
  }
`;

export const GET_KURSUS_IDS = (
  query: IPagedKursusQuery,
  totalCount: number,
) => {
  const { filters } = query;

  return gql`
    query kursusholdsIds {
      kursusholds(
        ${filters ? filters : ""}
        first: ${totalCount}
      ) {
        totalCount
        edges {
          node {
            id
          }
        }
      }
    }
  `;
};

export const GET_FMUPROEVE_DELTAGER_INFO = () => gql`
  query personByIdFmuProeveInfoQuery($id: UUID!) {
    person(id: $id) {
      kursusPerson{
        ${fmuProeveDeltagerInfoFields}
      }
    }
  }
`;
